// Route table for the Vite + React Router build.
// Mirrors main's Next.js app-router structure: each frontend has its own
// layout wrapper (FrontendLayout). The auth gate at `/` runs in routes/home.tsx.

import { Suspense, lazy, type ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RootLayout } from './routes/root';
import { HomePage } from './routes/home';
import { RootErrorBoundary } from './routes/error';
import { DeskLayoutRoute } from './routes/desk-layout';
import { OpsLayoutRoute } from './routes/ops-layout';
import { ComplyLayoutRoute } from './routes/comply-layout';
import { ViewLayoutRoute } from './routes/view-layout';
import { PortalLayoutRoute } from './routes/portal-layout';
import ComplyDashboardPage from '@/pages/comply/dashboard/page';
import AgentKanbanBoard from '@/components/agent/AgentKanbanBoard';

function Loading() {
    return <div className="p-8 text-body text-[rgb(var(--color-text-secondary))]">Loading…</div>;
}

// --- Imports (lazy) ---
const ComplyBreachRegisterPage = lazy(() => import('@/pages/comply/breach-register/page'));
const ComplyCallIntelligencePage = lazy(() => import('@/pages/comply/call-intelligence/page'));
const ComplyCmpReviewsPage = lazy(() => import('@/pages/comply/cmp-reviews/page'));
const ComplyClientBookPage = lazy(() => import('@/pages/comply/client-book/page'));
const ComplyClientBookClientPage = lazy(() => import('@/pages/comply/client-book/client/page'));
const ComplyConsumerDutyPage = lazy(() => import('@/pages/comply/consumer-duty/page'));
const ComplyDataFreshnessPage = lazy(() => import('@/pages/comply/data-freshness/page'));
const ComplyFairValuePage = lazy(() => import('@/pages/comply/fair-value/page'));
const ComplyFinCrimeKycPage = lazy(() => import('@/pages/comply/fin-crime-kyc/page'));
const ComplyMyPagePage = lazy(() => import('@/pages/comply/my-page/page'));
const ComplyOnboardingPage = lazy(() => import('@/pages/comply/onboarding/page'));
const ComplyOnboardingCasePage = lazy(() => import('@/pages/comply/onboarding/case/page'));
const ComplyOnboardingAnalyticsPage = lazy(
    () => import('@/pages/comply/onboarding/analytics/page'),
);
const ComplyPage = lazy(() => import('@/pages/comply/page'));
const ComplyRegCalendarPage = lazy(() => import('@/pages/comply/reg-calendar/page'));
const ComplyRegCapitalPage = lazy(() => import('@/pages/comply/reg-capital/page'));
const ComplySettingsPage = lazy(() => import('@/pages/comply/settings/page'));
const ComplySmcrDirectoryPage = lazy(() => import('@/pages/comply/smcr-directory/page'));
const ComplyOverdrawnEscalationsPage = lazy(
    () => import('@/pages/comply/overdrawn-escalations/page'),
);
const ComplySuitabilityPage = lazy(() => import('@/pages/comply/suitability/page'));
const ComplyTcTrackerPage = lazy(() => import('@/pages/comply/tc-tracker/page'));
const ComplyVulnerabilityPage = lazy(() => import('@/pages/comply/vulnerability/page'));
const ComplyVulnerabilityClientPage = lazy(
    () => import('@/pages/comply/vulnerability/client/page'),
);
const ComplyVulnerabilityEvidenceLibraryPage = lazy(
    () => import('@/pages/comply/vulnerability/evidence-library/page'),
);
const DeskActionsPage = lazy(() => import('@/pages/desk/actions/page'));
const DeskAgentsPage = lazy(() => import('@/pages/desk/agents/page'));
const DeskClientReviewsPage = lazy(() => import('@/pages/desk/client-reviews/page'));
const DeskClientReviewCockpitPage = lazy(() => import('@/pages/desk/client-reviews/client/page'));
const DeskClientBookPage = lazy(() => import('@/pages/desk/client-book/page'));
const DeskClientBookClientPage = lazy(() => import('@/pages/desk/client-book/client/page'));
const DeskCallsMeetingsPage = lazy(() => import('@/pages/desk/calls-meetings/page'));
const DeskClientKnowledgePage = lazy(() => import('@/pages/desk/client-knowledge/page'));
const DeskCorporateActionsPage = lazy(() => import('@/pages/desk/corporate-actions/page'));
const DeskFactFindPage = lazy(() => import('@/pages/desk/fact-find/page'));
const DeskFactFindClientPage = lazy(() => import('@/pages/desk/fact-find/client/page'));
const DeskFactFindRefreshPage = lazy(() => import('@/pages/desk/fact-find/client/refresh'));
const DeskFrameworkCompliancePage = lazy(() => import('@/pages/desk/framework-compliance/page'));
const DeskFrameworkModelPage = lazy(() => import('@/pages/desk/framework-compliance/model/page'));
const DeskBuyListPage = lazy(() => import('@/pages/desk/buy-list/page'));
const DeskRestrictedListPage = lazy(() => import('@/pages/desk/restricted-list/page'));
const DeskDeviationPage = lazy(() => import('@/pages/desk/deviation/page'));
const DeskBuyListImpactPage = lazy(() => import('@/pages/desk/buy-list-impact/page'));
const DeskVulnerabilityPage = lazy(() => import('@/pages/desk/vulnerability/page'));
const DeskMyBookPage = lazy(() => import('@/pages/desk/my-book/page'));
const DeskMyPagePage = lazy(() => import('@/pages/desk/my-page/page'));
const DeskOnboardingPage = lazy(() => import('@/pages/desk/onboarding/page'));
const DeskOnboardingCasePage = lazy(() => import('@/pages/desk/onboarding/case/page'));
const DeskOnboardingAnalyticsPage = lazy(() => import('@/pages/desk/onboarding/analytics/page'));
const DeskPage = lazy(() => import('@/pages/desk/page'));
const DeskOverdrawnCashPage = lazy(() => import('@/pages/desk/cash/overdrawn/page'));
const DeskPortfolioPage = lazy(() => import('@/pages/desk/portfolio/page'));
const DeskSettingsPage = lazy(() => import('@/pages/desk/settings/page'));
const DeskSuitabilityPage = lazy(() => import('@/pages/desk/suitability/page'));
const DeskSuitabilityAssessmentPage = lazy(
    () => import('@/pages/desk/suitability/assessment/page'),
);
const DeskVulnerabilityAssessmentPage = lazy(
    () => import('@/pages/desk/suitability/vulnerability/page'),
);
const DeskMandateLetterPage = lazy(() => import('@/pages/desk/suitability/mandate/page'));
const LoginPage = lazy(() => import('@/pages/login/page'));
// Workflows — the embedded @septima/workflow-ui editor surface. One page
// component shared across all four staff layouts (like AgentKanbanBoard).
const WorkflowsEditorPage = lazy(() => import('@/pages/workflows/page'));
const NotAuthorisedPage = lazy(() => import('@/pages/not-authorised/page'));
const OpsCashManagementPage = lazy(() => import('@/pages/ops/cash-management/page'));
const OpsOverdrawnCashPage = lazy(() => import('@/pages/ops/cash/overdrawn/page'));
const OpsClientBookPage = lazy(() => import('@/pages/ops/client-book/page'));
const OpsClientBookClientPage = lazy(() => import('@/pages/ops/client-book/client/page'));
const OpsCommandCentrePage = lazy(() => import('@/pages/ops/command-centre/page'));
const OpsCorporateActionsPage = lazy(() => import('@/pages/ops/corporate-actions/page'));
const OpsDocumentVaultPage = lazy(() => import('@/pages/ops/document-vault/page'));
const OpsFeesBillingPage = lazy(() => import('@/pages/ops/fees-billing/page'));
const OpsOnboardingPage = lazy(() => import('@/pages/ops/onboarding/page'));
const OpsOnboardingCasePage = lazy(() => import('@/pages/ops/onboarding/case/page'));
const OpsPage = lazy(() => import('@/pages/ops/page'));
const OpsReconciliationPage = lazy(() => import('@/pages/ops/reconciliation/page'));
const OpsReportingPage = lazy(() => import('@/pages/ops/reporting/page'));
const OpsSettingsPage = lazy(() => import('@/pages/ops/settings/page'));
const OpsTradeOperationsPage = lazy(() => import('@/pages/ops/trade-operations/page'));
const ViewAgentActivityLogPage = lazy(() => import('@/pages/view/agent-activity-log/page'));
const ViewBenchmarkingPage = lazy(() => import('@/pages/view/benchmarking/page'));
const ViewBusinessDevelopmentPage = lazy(() => import('@/pages/view/business-development/page'));
const ViewClientBookPage = lazy(() => import('@/pages/view/client-book/page'));
const ViewClientBookClientPage = lazy(() => import('@/pages/view/client-book/client/page'));
const ViewClientIntelligencePage = lazy(() => import('@/pages/view/client-intelligence/page'));
const ViewCustodianAnalyticsPage = lazy(() => import('@/pages/view/custodian-analytics/page'));
const ViewDataArchitecturePage = lazy(() => import('@/pages/view/data-architecture/page'));
const ViewFinancialPerformancePage = lazy(() => import('@/pages/view/financial-performance/page'));
const ViewFirmOverviewPage = lazy(() => import('@/pages/view/firm-overview/page'));
const ViewFundManagementPage = lazy(() => import('@/pages/view/fund-management/page'));
const ViewFundTrustDetailPage = lazy(() => import('@/pages/view/fund-management/trust/page'));
// IFC Governance and ManComm Programme pages are intentionally unwired (issue
// #208) — the page components in @/pages/view/ifc/* and @/pages/view/mancomm/*
// are kept in the tree so the views can be re-enabled by restoring the sidebar
// entries (frontends.ts) and the route registrations below.
const ViewInvestmentPerformancePage = lazy(
    () => import('@/pages/view/investment-performance/page'),
);
const ViewKpiPage = lazy(() => import('@/pages/view/kpi/page'));
const ViewOnboardingPage = lazy(() => import('@/pages/view/onboarding/page'));
const ViewOnboardingCasePage = lazy(() => import('@/pages/view/onboarding/case/page'));
const ViewOnboardingAnalyticsPage = lazy(() => import('@/pages/view/onboarding/analytics/page'));
const ViewOperationalIntelligencePage = lazy(
    () => import('@/pages/view/operational-intelligence/page'),
);
const ViewPage = lazy(() => import('@/pages/view/page'));
const ViewRegCalendarPage = lazy(() => import('@/pages/view/reg-calendar/page'));
const ViewRiskRegisterPage = lazy(() => import('@/pages/view/risk-register/page'));
const ViewAdministrationPage = lazy(() => import('@/pages/view/administration/page'));
// Client portal ("Septima Link") — the client-facing surface.
const PortalIndexPage = lazy(() => import('@/pages/portal/page'));
const PortalLoginPage = lazy(() => import('@/pages/portal/login/page'));
const PortalDashboardPage = lazy(() => import('@/pages/portal/dashboard/page'));
const PortalPortfolioPage = lazy(() => import('@/pages/portal/portfolio/page'));
const PortalHoldingPage = lazy(() => import('@/pages/portal/holding/page'));
const PortalCashPage = lazy(() => import('@/pages/portal/cash/page'));
const PortalExposurePage = lazy(() => import('@/pages/portal/exposure/page'));
const PortalDocumentsPage = lazy(() => import('@/pages/portal/documents/page'));
const ViewSettingsPage = lazy(() => import('@/pages/view/settings/page'));

function s(node: ReactElement) {
    return <Suspense fallback={<Loading />}>{node}</Suspense>;
}

export const router = createBrowserRouter([
    {
        path: '/',
        Component: RootLayout,
        ErrorBoundary: RootErrorBoundary,
        children: [
            { index: true, element: <HomePage /> },
            { path: 'login', element: s(<LoginPage />) },
            { path: 'not-authorised', element: s(<NotAuthorisedPage />) },

            {
                path: 'desk',
                Component: DeskLayoutRoute,
                children: [
                    { index: true, element: s(<DeskPage />) },
                    { path: 'actions', element: s(<DeskActionsPage />) },
                    { path: 'agents', element: s(<DeskAgentsPage />) },
                    { path: 'client-reviews', element: s(<DeskClientReviewsPage />) },
                    {
                        path: 'client-reviews/:clientId',
                        element: s(<DeskClientReviewCockpitPage />),
                    },
                    { path: 'calls-meetings', element: s(<DeskCallsMeetingsPage />) },
                    { path: 'client-book', element: s(<DeskClientBookPage />) },
                    { path: 'client-book/:clientId', element: s(<DeskClientBookClientPage />) },
                    { path: 'client-knowledge', element: s(<DeskClientKnowledgePage />) },
                    { path: 'corporate-actions', element: s(<DeskCorporateActionsPage />) },
                    { path: 'fact-find', element: s(<DeskFactFindPage />) },
                    { path: 'fact-find/:clientId', element: s(<DeskFactFindClientPage />) },
                    {
                        path: 'fact-find/:clientId/refresh',
                        element: s(<DeskFactFindRefreshPage />),
                    },
                    { path: 'framework-compliance', element: s(<DeskFrameworkCompliancePage />) },
                    {
                        path: 'framework-compliance/model/:modelId',
                        element: s(<DeskFrameworkModelPage />),
                    },
                    { path: 'buy-list', element: s(<DeskBuyListPage />) },
                    { path: 'restricted-list', element: s(<DeskRestrictedListPage />) },
                    { path: 'deviation/:deviationId', element: s(<DeskDeviationPage />) },
                    { path: 'buy-list-impact/:changeId', element: s(<DeskBuyListImpactPage />) },
                    { path: 'vulnerability', element: s(<DeskVulnerabilityPage />) },
                    { path: 'dashboard', element: s(<DeskMyBookPage />) },
                    { path: 'my-book', element: s(<DeskMyBookPage />) },
                    { path: 'my-page', element: s(<DeskMyPagePage />) },
                    { path: 'onboarding', element: s(<DeskOnboardingPage />) },
                    { path: 'onboarding/analytics', element: s(<DeskOnboardingAnalyticsPage />) },
                    { path: 'onboarding/case/:caseId', element: s(<DeskOnboardingCasePage />) },
                    { path: 'portfolio', element: s(<DeskPortfolioPage />) },
                    { path: 'cash/overdrawn', element: s(<DeskOverdrawnCashPage />) },
                    { path: 'settings', element: s(<DeskSettingsPage />) },
                    { path: 'suitability', element: s(<DeskSuitabilityPage />) },
                    {
                        path: 'suitability/assessment/:clientId',
                        element: s(<DeskSuitabilityAssessmentPage />),
                    },
                    {
                        path: 'suitability/vulnerability/:clientId',
                        element: s(<DeskVulnerabilityAssessmentPage />),
                    },
                    {
                        path: 'suitability/mandate/:clientId',
                        element: s(<DeskMandateLetterPage />),
                    },
                    { path: 'workflows', element: s(<WorkflowsEditorPage />) },
                ],
            },

            {
                path: 'ops',
                Component: OpsLayoutRoute,
                children: [
                    { index: true, element: s(<OpsPage />) },
                    { path: 'agents', element: s(<AgentKanbanBoard />) },
                    { path: 'cash-management', element: s(<OpsCashManagementPage />) },
                    { path: 'cash/overdrawn', element: s(<OpsOverdrawnCashPage />) },
                    { path: 'client-book', element: s(<OpsClientBookPage />) },
                    { path: 'client-book/:clientId', element: s(<OpsClientBookClientPage />) },
                    { path: 'dashboard', element: s(<OpsCommandCentrePage />) },
                    { path: 'command-centre', element: s(<OpsCommandCentrePage />) },
                    { path: 'corporate-actions', element: s(<OpsCorporateActionsPage />) },
                    { path: 'document-vault', element: s(<OpsDocumentVaultPage />) },
                    { path: 'fees-billing', element: s(<OpsFeesBillingPage />) },
                    { path: 'onboarding', element: s(<OpsOnboardingPage />) },
                    { path: 'onboarding/case/:caseId', element: s(<OpsOnboardingCasePage />) },
                    { path: 'reconciliation', element: s(<OpsReconciliationPage />) },
                    { path: 'reporting', element: s(<OpsReportingPage />) },
                    { path: 'settings', element: s(<OpsSettingsPage />) },
                    { path: 'trade-operations', element: s(<OpsTradeOperationsPage />) },
                    { path: 'workflows', element: s(<WorkflowsEditorPage />) },
                ],
            },

            {
                path: 'comply',
                Component: ComplyLayoutRoute,
                children: [
                    { index: true, element: s(<ComplyPage />) },
                    { path: 'agents', element: s(<AgentKanbanBoard />) },
                    { path: 'breach-register', element: s(<ComplyBreachRegisterPage />) },
                    { path: 'call-intelligence', element: s(<ComplyCallIntelligencePage />) },
                    { path: 'client-book', element: s(<ComplyClientBookPage />) },
                    {
                        path: 'client-book/:clientId',
                        element: s(<ComplyClientBookClientPage />),
                    },
                    { path: 'cmp-reviews', element: s(<ComplyCmpReviewsPage />) },
                    { path: 'consumer-duty', element: s(<ComplyConsumerDutyPage />) },
                    { path: 'dashboard', element: <ComplyDashboardPage /> },
                    { path: 'data-freshness', element: s(<ComplyDataFreshnessPage />) },
                    { path: 'fair-value', element: s(<ComplyFairValuePage />) },
                    { path: 'fin-crime-kyc', element: s(<ComplyFinCrimeKycPage />) },
                    { path: 'my-page', element: s(<ComplyMyPagePage />) },
                    { path: 'onboarding', element: s(<ComplyOnboardingPage />) },
                    {
                        path: 'overdrawn-escalations',
                        element: s(<ComplyOverdrawnEscalationsPage />),
                    },
                    { path: 'onboarding/analytics', element: s(<ComplyOnboardingAnalyticsPage />) },
                    { path: 'onboarding/case/:caseId', element: s(<ComplyOnboardingCasePage />) },
                    { path: 'reg-calendar', element: s(<ComplyRegCalendarPage />) },
                    { path: 'reg-capital', element: s(<ComplyRegCapitalPage />) },
                    { path: 'settings', element: s(<ComplySettingsPage />) },
                    { path: 'smcr-directory', element: s(<ComplySmcrDirectoryPage />) },
                    { path: 'suitability', element: s(<ComplySuitabilityPage />) },
                    { path: 'tc-tracker', element: s(<ComplyTcTrackerPage />) },
                    { path: 'vulnerability', element: s(<ComplyVulnerabilityPage />) },
                    {
                        path: 'vulnerability/client/:clientId',
                        element: s(<ComplyVulnerabilityClientPage />),
                    },
                    {
                        path: 'vulnerability/evidence-library',
                        element: s(<ComplyVulnerabilityEvidenceLibraryPage />),
                    },
                    { path: 'workflows', element: s(<WorkflowsEditorPage />) },
                ],
            },

            {
                path: 'view',
                Component: ViewLayoutRoute,
                children: [
                    { index: true, element: s(<ViewPage />) },
                    { path: 'agent-activity-log', element: s(<ViewAgentActivityLogPage />) },
                    { path: 'agents', element: s(<AgentKanbanBoard />) },
                    { path: 'benchmarking', element: s(<ViewBenchmarkingPage />) },
                    { path: 'business-development', element: s(<ViewBusinessDevelopmentPage />) },
                    { path: 'client-book', element: s(<ViewClientBookPage />) },
                    { path: 'client-book/:clientId', element: s(<ViewClientBookClientPage />) },
                    { path: 'client-intelligence', element: s(<ViewClientIntelligencePage />) },
                    { path: 'custodian-analytics', element: s(<ViewCustodianAnalyticsPage />) },
                    { path: 'data-architecture', element: s(<ViewDataArchitecturePage />) },
                    { path: 'financial-performance', element: s(<ViewFinancialPerformancePage />) },
                    { path: 'dashboard', element: s(<ViewFirmOverviewPage />) },
                    { path: 'firm-overview', element: s(<ViewFirmOverviewPage />) },
                    { path: 'fund-management', element: s(<ViewFundManagementPage />) },
                    {
                        path: 'fund-management/:trustId',
                        element: s(<ViewFundTrustDetailPage screen="dashboard" />),
                    },
                    {
                        path: 'fund-management/:trustId/policy',
                        element: s(<ViewFundTrustDetailPage screen="policy" />),
                    },
                    {
                        path: 'fund-management/:trustId/gearing',
                        element: s(<ViewFundTrustDetailPage screen="gearing" />),
                    },
                    {
                        path: 'fund-management/:trustId/discount',
                        element: s(<ViewFundTrustDetailPage screen="discount" />),
                    },
                    {
                        path: 'fund-management/:trustId/register',
                        element: s(<ViewFundTrustDetailPage screen="register" />),
                    },
                    {
                        path: 'fund-management/:trustId/rns',
                        element: s(<ViewFundTrustDetailPage screen="rns" />),
                    },
                    {
                        path: 'fund-management/:trustId/aifmd',
                        element: s(<ViewFundTrustDetailPage screen="aifmd" />),
                    },
                    {
                        path: 'fund-management/:trustId/kid',
                        element: s(<ViewFundTrustDetailPage screen="kid" />),
                    },
                    {
                        path: 'fund-management/:trustId/board-pack',
                        element: s(<ViewFundTrustDetailPage screen="board-pack" />),
                    },
                    {
                        path: 'fund-management/:trustId/continuation',
                        element: s(<ViewFundTrustDetailPage screen="continuation" />),
                    },
                    {
                        path: 'fund-management/:trustId/research',
                        element: s(<ViewFundTrustDetailPage screen="research" />),
                    },
                    // IFC Governance + ManComm Programme are unwired (issue #208).
                    // The page components are kept; saved/direct URLs to these
                    // prefixes (and their sub-paths) redirect to the View home
                    // rather than rendering a blank screen (no catch-all route).
                    { path: 'ifc/*', element: <Navigate to="/view/firm-overview" replace /> },
                    { path: 'mancomm/*', element: <Navigate to="/view/firm-overview" replace /> },
                    {
                        path: 'investment-performance',
                        element: s(<ViewInvestmentPerformancePage />),
                    },
                    { path: 'kpi', element: s(<ViewKpiPage />) },
                    { path: 'onboarding', element: s(<ViewOnboardingPage />) },
                    {
                        path: 'onboarding/analytics',
                        element: s(<ViewOnboardingAnalyticsPage />),
                    },
                    { path: 'onboarding/case/:caseId', element: s(<ViewOnboardingCasePage />) },
                    {
                        path: 'operational-intelligence',
                        element: s(<ViewOperationalIntelligencePage />),
                    },
                    { path: 'reg-calendar', element: s(<ViewRegCalendarPage />) },
                    { path: 'risk-register', element: s(<ViewRiskRegisterPage />) },
                    { path: 'administration', element: s(<ViewAdministrationPage />) },
                    { path: 'settings', element: s(<ViewSettingsPage />) },
                    { path: 'workflows', element: s(<WorkflowsEditorPage />) },
                ],
            },
            {
                // Client portal — its own chrome (PortalLayoutRoute), gated on a
                // portal credential session, no staff sidebar/agent panel.
                path: 'portal',
                Component: PortalLayoutRoute,
                children: [
                    { index: true, element: s(<PortalIndexPage />) },
                    { path: 'login', element: s(<PortalLoginPage />) },
                    { path: 'dashboard', element: s(<PortalDashboardPage />) },
                    { path: 'portfolio', element: s(<PortalPortfolioPage />) },
                    { path: 'holdings/:isin', element: s(<PortalHoldingPage />) },
                    { path: 'cash', element: s(<PortalCashPage />) },
                    { path: 'exposure', element: s(<PortalExposurePage />) },
                    { path: 'documents', element: s(<PortalDocumentsPage />) },
                    // Per-client credential link `/portal/<slug>` — reuses the
                    // login page (slug fixed from the URL). React Router ranks
                    // the static paths above this dynamic segment, so only a
                    // credential slug falls through here.
                    { path: ':slug', element: s(<PortalLoginPage />) },
                ],
            },
        ],
    },
]);
